import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  Paper,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";

import AdminPayoutDetailDialog, { PayoutRequest } from "./AdminPayoutDetails";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";

// ── Types ─────────────────────────────────────────────────────────────────────

interface PaginatedResponse {
  results: PayoutRequest[];
  count: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Rejected", value: "REJECTED" },
];

const statusConfig = (status: PayoutRequest["status"]) => {
  if (status === "PENDING")
    return { label: "Pending", bgcolor: "#fff8e1", color: "#f59e0b" };
  if (status === "APPROVED")
    return { label: "Approved", bgcolor: "#e8f5e9", color: "#4caf50" };
  if (status === "REJECTED")
    return { label: "Rejected", bgcolor: "#ffebee", color: "#f44336" };
  return { label: "Completed", bgcolor: "#e3f2fd", color: "#1565c0" };
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const getInitials = (name: string | undefined) =>
  (name ?? "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

const MOBILE_METHODS = ["ECOCASH", "INNBUCKS", "OMARI", "ONEMONEY"];
const METHOD_LABELS: Record<string, string> = {
  ECOCASH: "Ecocash",
  INNBUCKS: "InnBucks",
  OMARI: "Omari",
  ONEMONEY: "OneMoney",
};

const accountSummary = (payout: PayoutRequest): string => {
  const details = payout.payout_account_details;
  if (details) {
    const name = details.account_name ? ` · ${details.account_name}` : "";
    if (MOBILE_METHODS.includes(details.method))
      return `${METHOD_LABELS[details.method] ?? details.method} — ${details.ecocash_number ?? ""}${name}`;
    return `${details.bank_name ?? "Bank"} — ${details.account_number ?? ""}${name}`;
  }
  const snap = payout.payout_account_snapshot;
  if (MOBILE_METHODS.includes(snap.method))
    return `${METHOD_LABELS[snap.method] ?? snap.method} — ${snap.masked_ecocash_number ?? ""}`;
  return `${snap.bank_name ?? "Bank"} — ${snap.masked_account_number ?? ""}`;
};
// ── Component ─────────────────────────────────────────────────────────────────

export default function AdminPayoutRequestsTable() {
  const [payouts, setPayouts] = useState<PayoutRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedPayout, setSelectedPayout] = useState<PayoutRequest | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", String(rowsPerPage));
      params.append("offset", String(page * rowsPerPage));
      if (statusFilter) params.append("status", statusFilter);

      const res = await apiService.get<PaginatedResponse>(
        `/api/wallet/admin/payout-requests/?${params.toString()}`,
      );
      setPayouts(res.data.results ?? []);
      setTotalCount(res.data.count ?? 0);
    } catch (err) {
      console.error("Failed to load payout requests", err);
      toast.error("Failed to load payout requests. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchPayouts();
    const interval = setInterval(fetchPayouts, 30000);
    return () => clearInterval(interval);
  }, [fetchPayouts]);

  const handleOpenDetail = (payout: PayoutRequest) => {
    setSelectedPayout(payout);
    setDialogOpen(true);
  };

  const handlePayoutUpdated = (updated: PayoutRequest) => {
    // Update in list
    setPayouts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    // Keep dialog open with updated data so admin can continue (e.g. upload proof then complete)
    setSelectedPayout(updated);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Status filter tabs */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          borderBottom: "1px solid #eee",
          pb: 1,
        }}
      >
        {STATUS_TABS.map((tab) => (
          <Typography
            key={tab.value}
            variant="body2"
            onClick={() => handleStatusFilterChange(tab.value)}
            sx={{
              cursor: "pointer",
              fontWeight: statusFilter === tab.value ? 700 : 400,
              color:
                statusFilter === tab.value ? "text.primary" : "text.secondary",
              borderBottom:
                statusFilter === tab.value
                  ? "2px solid #1a237e"
                  : "2px solid transparent",
              pb: 0.5,
              mb: "-1px",
              whiteSpace: "nowrap",
            }}
          >
            {tab.label}
          </Typography>
        ))}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  "Partner",
                  "Amount",
                  "Method / Account",
                  "Status",
                  "Requested",
                  "",
                ].map((col, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      py: 1.5,
                      bgcolor: "#fff",
                      borderBottom: "1px solid #eee",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    No payout requests found.
                  </TableCell>
                </TableRow>
              ) : (
                payouts.map((payout) => {
                  const { label, bgcolor, color } = statusConfig(payout.status);
                  return (
                    <TableRow
                      key={payout.id}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:last-child td": { border: 0 },
                      }}
                      onClick={() => handleOpenDetail(payout)}
                    >
                      {/* Partner */}
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: 11,
                              bgcolor: "#1a237e",
                            }}
                          >
                            {getInitials(payout.user_name)}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              fontSize="13px"
                            >
                              {payout.user_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontSize="11px"
                            >
                              {payout.user_email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Amount */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontSize="13px"
                        >
                          ${parseFloat(payout.amount).toFixed(2)}
                        </Typography>
                      </TableCell>

                      {/* Method / Account */}
                      <TableCell>
                        <Typography variant="body2" fontSize="12px">
                          {accountSummary(payout)}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={label}
                          size="small"
                          sx={{
                            bgcolor,
                            color,
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            height: 22,
                          }}
                        />
                      </TableCell>

                      {/* Date */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontSize="12px"
                          color="text.secondary"
                        >
                          {formatDate(payout.requested_at)}
                        </Typography>
                      </TableCell>

                      {/* View action */}
                      <TableCell
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 40 }}
                      >
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDetail(payout)}
                          >
                            <Visibility
                              fontSize="small"
                              sx={{ color: "#888" }}
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />

      <AdminPayoutDetailDialog
        open={dialogOpen}
        payout={selectedPayout}
        onClose={() => {
          setDialogOpen(false);
          fetchPayouts(); // refresh list after any action
        }}
        onUpdated={handlePayoutUpdated}
      />
    </Box>
  );
}
